/**
 * liftDisplayPreferenceCache — dependency-free core for the per-DC lift display
 * preference (CUSTOMER vs REHRIG).
 *
 * This module holds the pure pieces (constants, the label helper, and the
 * process-wide cache) with NO React / redux / API imports, so any component
 * (e.g. the Lift Mapping settings tab) can clear the cache after changing the
 * preference without pulling in the data-fetching hook's dependencies. The
 * React hook (`useLiftDisplayPreference`) builds on top of this.
 *
 * @module liftDisplayPreferenceCache
 */

export const LIFT_PREF_CUSTOMER = "CUSTOMER";
export const LIFT_PREF_REHRIG = "REHRIG";
// Default when a DC has no stored preference: REHRIG (grids show only the Rehrig
// lift id until an admin opts into customer aliases). Kept in sync with the
// backend defaults (/v2/admin/locations layer + diotLiftMapping).
export const DEFAULT_LIFT_PREFERENCE = LIFT_PREF_REHRIG;

// Process-wide cache: locationId (string) -> "CUSTOMER" | "REHRIG". Shared
// across every grid so a DC's preference is fetched at most once (until cleared
// by a preference change in the settings tab).
const _prefCache = new Map();

export function getCachedPreference(locationId) {
  if (locationId == null) return undefined;
  return _prefCache.get(String(locationId));
}

export function setCachedPreference(locationId, preference) {
  if (locationId == null) return;
  _prefCache.set(String(locationId), preference);
}

/** Clear the whole cache (call after a preference change so grids reload it). */
export function clearLiftPreferenceCache() {
  _prefCache.clear();
}

/**
 * Fields a lift row may carry the Rehrig id under, in preference order.
 * NOTE: `liftName` is intentionally NOT included. The display preference toggles
 * strictly between the Rehrig lift id and the customer alias; `liftName` is a
 * separate concept (e.g. Lift Maintenance labels by name) and must never be
 * substituted here.
 */
const _REHRIG_KEYS = ["liftId", "lift_id", "rehrigLiftId", "LIFT_ID"];
/** Fields a lift row may carry the customer alias under, in preference order. */
const _ALIAS_KEYS = [
  "customerLiftAlias",
  "customer_lift_alias",
  "customerAlias",
  "liftAlias",
  "CUSTOMER_LIFT_ALIAS",
];

function _firstNonEmpty(row, keys) {
  if (!row) return "";
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
}

/**
 * Normalize a raw preference value to CUSTOMER | REHRIG (default REHRIG).
 * Only an explicit "CUSTOMER" yields CUSTOMER; everything else (including a
 * missing/blank/unknown value) falls back to the REHRIG default.
 */
export function normalizeLiftPreference(pref) {
  const value = typeof pref === "string" ? pref.trim().toUpperCase() : "";
  return value === LIFT_PREF_CUSTOMER ? LIFT_PREF_CUSTOMER : DEFAULT_LIFT_PREFERENCE;
}

/**
 * Compute the label to DISPLAY for a lift given the DC preference:
 *   - REHRIG   -> the Rehrig id only.
 *   - CUSTOMER -> the alias if present, else the Rehrig id (alias-or-Rehrig).
 *
 * Display-only: callers keep navigating / keying by the real Rehrig `liftId`.
 *
 * @param {object} row - a lift-bearing row (tolerant of field-name variants)
 * @param {string} preference - CUSTOMER | REHRIG
 * @returns {string} the label to show (may be "" when the row has no lift)
 */
export function computeLiftLabel(row, preference) {
  const rehrig = _firstNonEmpty(row, _REHRIG_KEYS);
  if (normalizeLiftPreference(preference) === LIFT_PREF_REHRIG) return rehrig;
  const alias = _firstNonEmpty(row, _ALIAS_KEYS);
  return alias || rehrig;
}
